import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { anchorOf, linkKind, parseBlocks, parseInline } from "../../lib/guideMarkdown";
import { imageSrc, longDay } from "../../lib/guide";

/**
 * One article of the guide (19.10.2026), in the admin and in the portal.
 * The Markdown is read into blocks and drawn as elements — never as HTML.
 * A link to another article calls `onOpen(id, anchor)`; a heading carries
 * its anchor, so `#…` in the address scrolls to it.
 */
export default function GuideArticle({ article, onOpen, anchor, footer }) {
  const blocks = parseBlocks(article?.body);
  const anchors = (article?.sections || []).map((s) => s.anchor);
  let h2 = 0;

  useEffect(() => {
    if (!anchor) return undefined;
    const t = setTimeout(() => document.getElementById(`ghid-${anchor}`)?.scrollIntoView({ block: "start" }), 60);
    return () => clearTimeout(t);
  }, [anchor, article?.id]);

  if (!article) return null;
  return (
    <article className="min-w-0 text-[15px] leading-relaxed text-[#373A45]" data-testid="guide-article">
      <h1 className="font-display text-2xl font-bold leading-tight text-[#373A45]">{article.title}</h1>
      <p className="mt-3 rounded-xl bg-[#F0E7F4]/60 px-4 py-3 text-sm text-[#4B5160]">
        <span className="font-semibold text-[#7A4986]">Pe scurt: </span>{article.summary}
      </p>
      <div className="mt-5 grid gap-4">
        {blocks.map((b, i) => {
          if (b.type === "h2") {
            const id = anchors[h2] || anchorOf(b.text);
            h2 += 1;
            return <h2 key={i} id={`ghid-${id}`} className="mt-3 scroll-mt-20 font-display text-lg font-bold text-[#373A45]">{b.text}</h2>;
          }
          if (b.type === "h3") return <h3 key={i} className="mt-1 text-base font-semibold text-[#373A45]">{b.text}</h3>;
          if (b.type === "list") {
            const Tag = b.ordered ? "ol" : "ul";
            return (
              <Tag key={i} className={`grid gap-1.5 pl-5 ${b.ordered ? "list-decimal" : "list-disc"} marker:text-[#965EA7]`}>
                {b.items.map((it, j) => <li key={j}><Inline text={it} onOpen={onOpen} /></li>)}
              </Tag>
            );
          }
          if (b.type === "callout") {
            return (
              <div key={i} className="flex gap-2.5 rounded-xl border border-[#d99a2b]/30 bg-[#d99a2b]/[0.07] px-4 py-3 text-sm">
                <Info size={16} className="mt-0.5 shrink-0 text-[#b07a1c]" aria-hidden="true" />
                <div><Inline text={b.text} onOpen={onOpen} /></div>
              </div>
            );
          }
          if (b.type === "table") {
            return (
              <div key={i} className="overflow-x-auto rounded-xl border border-[#E5E7EB]">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-[#F5F6F6] text-left">
                    <tr>{b.head.map((h, j) => <th key={j} className="px-3 py-2 font-semibold text-[#373A45]"><Inline text={h} onOpen={onOpen} /></th>)}</tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r} className="border-t border-[#E5E7EB] align-top">
                        {row.map((c, j) => <td key={j} className="px-3 py-2"><Inline text={c} onOpen={onOpen} /></td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          if (b.type === "img") {
            return (
              <figure key={i} className="overflow-hidden rounded-xl border border-[#E5E7EB]">
                <img src={imageSrc(b.src)} alt={b.alt} loading="lazy" className="block w-full" />
                {b.alt && <figcaption className="border-t border-[#E5E7EB] bg-[#F5F6F6] px-3 py-1.5 text-xs text-[#6B7280]">{b.alt}</figcaption>}
              </figure>
            );
          }
          return <p key={i}><Inline text={b.text} onOpen={onOpen} /></p>;
        })}
      </div>

      {(article.related || []).length > 0 && (
        <section className="mt-8 border-t border-[#E5E7EB] pt-4">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Articole legate</h2>
          <ul className="mt-2 grid gap-1">
            {article.related.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => onOpen?.(r.id, "")} className="text-left text-sm font-semibold text-[#7A4986] hover:underline">
                  {r.title}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="mt-6 text-xs text-[#9CA3AF]">Actualizat pe {longDay(article.updated)}</p>
      {footer}
    </article>
  );
}

function Inline({ text, onOpen }) {
  return parseInline(text).map((piece, i) => {
    if (piece.type === "strong") return <strong key={i} className="font-semibold">{piece.text}</strong>;
    if (piece.type === "em") return <em key={i}>{piece.text}</em>;
    if (piece.type === "code") return <code key={i} className="rounded bg-[#F5F6F6] px-1 py-0.5 text-[13px]">{piece.text}</code>;
    if (piece.type === "link") {
      const k = linkKind(piece.href);
      const cls = "font-semibold text-[#7A4986] underline-offset-2 hover:underline";
      if (k.kind === "article") return <button key={i} type="button" onClick={() => onOpen?.(k.id, k.anchor)} className={cls}>{piece.text}</button>;
      if (k.kind === "internal") return <Link key={i} to={k.to} className={cls}>{piece.text}</Link>;
      if (k.kind === "external") return <a key={i} href={k.href} target="_blank" rel="noopener noreferrer" className={cls}>{piece.text}</a>;
      return <span key={i}>{piece.text}</span>;
    }
    return <React.Fragment key={i}>{piece.text}</React.Fragment>;
  });
}
